import React from "react";
import { Trans } from "react-i18next";

import i18n from "@/i18n";

import useSendEmailJobRecipientPlaceholderValues from "@/hooks/notifications/useSendEmailJobRecipientPlaceholderValues";

import DialogModal from "@/components/dialogModal";
import Avatar from "@/components/avatar";
import { PlaceholderFieldBadge } from "@/components/tiptap-editor/extensions/placeholder";
import { Badge } from "@/components/ui/badge";
import { Check, TriangleAlert } from "lucide-react";
import { cn } from "../utils";

const { t } = i18n;

const UserCommRecipientPlaceholderValuesGrid = ({
  recipients,
  editor,
  onCancel,
}) => {
  const {
    isLoading,
    placeholderStatuses,
    placeholdersUsed,
    missingPlaceholderValuesCount,
    supportedPlaceholderFields,
  } = useSendEmailJobRecipientPlaceholderValues(recipients, editor);

  const placeholderById = Object.fromEntries(
    supportedPlaceholderFields.map((field) => [field.id, field]),
  );

  const hasAllPlaceholderValues = missingPlaceholderValuesCount === 0;

  return (
    <DialogModal
      variant={hasAllPlaceholderValues ? "success" : "warning"}
      title={
        hasAllPlaceholderValues ? (
          t(
            "send_email_jobs.placeholder_status.no_missing_placeholder_values.title",
          )
        ) : (
          <Trans
            i18nKey="send_email_jobs.placeholder_status.missing_placeholder_values.body"
            values={{ count: missingPlaceholderValuesCount }}
          />
        )
      }
      isLoading={isLoading}
      size="xl"
      onCancel={onCancel}
      submitText={null}
      bodyRenderer={() => (
        <div>
          <table className="w-full table-auto">
            <thead>
              <tr className="[&_th]:px-2 [&_th]:py-4">
                <th width={30} className="sticky">
                  {/* Status */}
                </th>
                <th>{/* Recipient name */}</th>
                {placeholdersUsed.map((placeholderId) => {
                  const { category, description } =
                    placeholderById[placeholderId] || {};
                  return (
                    <th key={placeholderId}>
                      <PlaceholderFieldBadge
                        id={placeholderId}
                        category={category}
                        title={description}
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {recipients.map((r) => {
                const { avatarId, name, avatarType, hasMissingValues } =
                  placeholderStatuses[r.id];

                return (
                  <tr
                    key={r.id}
                    className={cn("odd:bg-slate-50 [&_td]:px-2 [&_td]:py-4")}
                  >
                    <td>
                      {hasMissingValues ? (
                        <TriangleAlert size={16} className="text-destructive" />
                      ) : (
                        <Check size={16} className="text-slate-300" />
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-4">
                        <Avatar
                          size="sm"
                          id={avatarId}
                          type={avatarType}
                          name={name}
                        />{" "}
                        <strong>{name}</strong>
                      </div>
                    </td>
                    {placeholdersUsed.map((placeholderId) => {
                      const placeholderValue =
                        placeholderStatuses[r.id]?.values?.[placeholderId];

                      return (
                        <td key={placeholderId}>
                          {placeholderValue === null ? (
                            <div className="flex">
                              <Badge
                                variant="outline-destructive"
                                className="flex items-center gap-2"
                              >
                                <TriangleAlert
                                  size={12}
                                  className="text-destructive"
                                />
                                {t(
                                  "investments.my_records.grid.missing_values",
                                )}
                              </Badge>
                            </div>
                          ) : (
                            placeholderValue
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    />
  );
};

export default UserCommRecipientPlaceholderValuesGrid;
